import { useCallback, useState } from 'react'
import { CarePartnerCreateDTO, CarePartnerUpdateDTO } from '../client'
import { useCreateCarePartner } from './useCreateCarePartner'
import { useUpdateCandidateCarePartner } from './useUpdateCandidateCarePartner'
import { useUpdateCarePartner } from './useUpdateCarePartner'

export const useCreateAndAssignCarePartner = () => {
    const { createCarePartner, loading: creatingCarePartner } = useCreateCarePartner()
    const { updateCandidateCarePartner, loading: updatingCandidate } = useUpdateCandidateCarePartner()
    const { updateCarePartner, loading: updatingCarePartner } = useUpdateCarePartner()

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const createAndAssign = useCallback(
        async (carePartnerData: CarePartnerCreateDTO, candidateId?: string) => {
            setLoading(true)
            setError(null)

            try {
                // Step 1: Create the care partner first (we need the ID)
                const carePartner = await createCarePartner(carePartnerData)
                if (!carePartner) {
                    throw new Error('Failed to create care partner')
                }

                // Step 2: Associate with candidate if provided
                if (candidateId) {
                    try {
                        await updateCandidateCarePartner(candidateId, carePartner.id, carePartner.organizationId)
                    } catch (assignError) {
                        // Don't fail the entire operation if association fails
                        // The care partner was still created successfully
                    }
                }

                return carePartner
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to create care partner'
                setError(errorMessage)
                throw err
            } finally {
                setLoading(false)
            }
        },
        [createCarePartner, updateCandidateCarePartner]
    )

    const updateCarePartnerData = useCallback(
        async (carePartnerId: string, carePartnerData: CarePartnerUpdateDTO) => {
            setLoading(true)
            setError(null)

            try {
                const updatedCarePartner = await updateCarePartner(carePartnerId, carePartnerData)
                return updatedCarePartner
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to update care partner'
                setError(errorMessage)
                throw err
            } finally {
                setLoading(false)
            }
        },
        [updateCarePartner]
    )

    return {
        createAndAssign,
        updateCarePartnerData,
        loading: loading || creatingCarePartner || updatingCandidate || updatingCarePartner,
        error,
        creatingCarePartner,
        updatingCandidate,
        updatingCarePartner
    }
}
